import type { LucideIcon } from "lucide-react";
import { CheckCircle2, CircleDashed, CircleX, Cookie, Settings } from "lucide-react";
import type { ComponentProps } from "react";
import { Fragment, useEffect, useState } from "react";
import type { Entries } from "type-fest";
import * as CookieConsent from "vanilla-cookieconsent";
import { z } from "zod";

import { useDi } from "~/entities/di";
import type { Category } from "~/shared/cookie";
import { CATEGORY_SERVICES } from "~/shared/cookie";
import type { Fn } from "~/shared/fp";
import { formatDateTime, Tk, useTranslation2 } from "~/shared/i18n";
import { UNDERSCORE } from "~/shared/str";
import { cn } from "~/shared/ui";
import { Badge } from "~/shared/ui/badge";
import { Button } from "~/shared/ui/button";
import { Dd, Dl, Dt } from "~/shared/ui/description-list";
import { Popover, PopoverContent, PopoverTrigger } from "~/shared/ui/popover";
import { Separator } from "~/shared/ui/separator";

const categories = {
  necessary: Tk.CONSENT_PREFERENCES_MODAL_SECTION_NECESSARY_TITLE,
  analytics: Tk.CONSENT_PREFERENCES_MODAL_SECTION_ANALYTICS_TITLE,
  advertisement: Tk.CONSENT_PREFERENCES_MODAL_SECTION_ADVERTISING_TITLE,
  functionality: Tk.CONSENT_PREFERENCES_MODAL_SECTION_FUNCTIONALITY_TITLE,
  security: Tk.CONSENT_PREFERENCES_MODAL_SECTION_SECURITY_TITLE,
} satisfies Record<Category, Tk>;

const zCategoryStatus = z.enum(["ALLOWED", "PARTIALLY_ALLOWED", "BLOCKED"]);
const CategoryStatus = zCategoryStatus.Enum;
type CategoryStatus = z.infer<typeof zCategoryStatus>;

const STATUS_ICON_BY_CATEGORY_STATUS = {
  ALLOWED: {
    Icon: CheckCircle2,
    className: "text-green-600",
  },
  PARTIALLY_ALLOWED: {
    Icon: CircleDashed,
    className: "text-amber-600",
  },
  BLOCKED: {
    Icon: CircleX,
    className: "text-red-600",
  },
} satisfies Record<
  CategoryStatus,
  {
    Icon: LucideIcon;
    className: string;
  }
>;

function StatusIcon({ status }: { status: CategoryStatus }) {
  const { Icon, className } = STATUS_ICON_BY_CATEGORY_STATUS[status];

  return <Icon className={cn("size-4", className)} />;
}

const STATUS_BADGE_VARIANT_BY_CATEGORY_STATUS = {
  ALLOWED: "secondary",
  PARTIALLY_ALLOWED: "outline",
  BLOCKED: "destructive",
} satisfies Record<CategoryStatus, ComponentProps<typeof Badge>["variant"]>;

type StatusBadgeProps = Omit<ComponentProps<typeof Badge>, "variant" | "children"> & {
  status: CategoryStatus;
};

function StatusBadge({ status, ...props }: StatusBadgeProps) {
  const {
    postproc: { tc },
  } = useTranslation2();

  return (
    <Badge variant={STATUS_BADGE_VARIANT_BY_CATEGORY_STATUS[status]} {...props}>
      {tc(["CONSENT_SUMMARY", status].join(UNDERSCORE))}
    </Badge>
  );
}

function getCategoryStatuses() {
  return Object.fromEntries(
    (Object.entries(CATEGORY_SERVICES) as Entries<typeof CATEGORY_SERVICES>).map(
      ([category, services = {}]) => {
        const services_ = Object.keys(services);

        const numberOfAllowedServices = services_.filter((service) =>
          CookieConsent.acceptedService(service, category),
        ).length;

        return [
          category,
          (numberOfAllowedServices === services_.length
            ? CategoryStatus.ALLOWED
            : numberOfAllowedServices > 0
              ? CategoryStatus.PARTIALLY_ALLOWED
              : CategoryStatus.BLOCKED) satisfies CategoryStatus,
        ] as const;
      },
    ),
  ) as Record<Category, CategoryStatus>;
}

function ConsentSummary() {
  const {
    postproc: { tc },
  } = useTranslation2();

  const { eventBus } = useDi();

  const [categoryStatuses, setCategoryStatuses] = useState(getCategoryStatuses);

  useEffect(() => {
    const params = [
      "cookieConsentChanged",
      () => {
        setCategoryStatuses(getCategoryStatuses());
      },
    ] as const;
    eventBus.on(...params);

    return () => {
      eventBus.off(...params);
    };
  }, [eventBus]);

  if (Object.keys(CookieConsent.getCookie()).length === 0) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          size="auto"
          className="absolute bottom-20 left-2 size-16 rounded-full"
        >
          <Cookie className="size-3/4" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="flex flex-col gap-4  max-w-(--radix-popover-content-available-width) w-fit">
        <div className="flex flex-col gap-1">
          <h3 className="font-semibold">{tc(Tk.CONSENT_SUMMARY_TITLE)}</h3>
          <p className="  text-sm text-muted-foreground">{tc(Tk.CONSENT_SUMMARY_DESCRIPTION)}</p>
        </div>
        <Separator />
        <div className="grid grid-cols-[auto_1fr_auto] gap-2">
          {(Object.entries(categories) as Entries<typeof categories>).map(([name, tk]) => {
            const status = categoryStatuses[name];
            return (
              <Fragment key={name}>
                <StatusIcon status={status} />
                <span className="text-sm">{tc(tk)}</span>
                <StatusBadge status={status} className="justify-self-end" />
              </Fragment>
            );
          })}
        </div>
        <Separator />
        <ConsentFields />
        <Button onClick={CookieConsent.showPreferences}>
          <Settings />
          {tc(Tk.CONSENT_PREFERENCES_MODAL_TITLE)}
        </Button>
      </PopoverContent>
    </Popover>
  );
}

function ConsentFields() {
  const {
    translation: {
      i18n: { language },
    },
    postproc: { tc },
  } = useTranslation2();

  type Fields = Partial<{
    [K in keyof CookieConsent.CookieValue]: {
      tk: Tk;
      formatField: Fn<[value: CookieConsent.CookieValue[K]], string>;
    };
  }>;

  const fields = {
    consentId: {
      tk: Tk.CONSENT_ID,
      formatField: (value) => value,
    },
    lastConsentTimestamp: {
      tk: Tk.LAST_MODIFIED,
      formatField: (value) => formatDateTime(language, new Date(value)),
    },
  } satisfies Fields;

  return (
    <Dl gap="0.25rem">
      {(Object.entries(fields) as Entries<typeof fields>).map(
        ([field, { tk, formatField }], index) => {
          const cookieValue = CookieConsent.getCookie(field);

          return (
            <Fragment key={index}>
              <Dt className="text-muted-foreground text-sm font-semibold">{tc(tk)}</Dt>
              <Dd className="font-mono text-sm">{formatField(cookieValue)}</Dd>
            </Fragment>
          );
        },
      )}
    </Dl>
  );
}

export { ConsentSummary };
